import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { CollaborationPresenceUser } from '@/hooks/use-collaboration-presence';
import { cn } from '@/lib/utils';

type PresenceAvatarStackProps = {
    users: CollaborationPresenceUser[];
    maxVisible: number;
    size?: 'sm' | 'md';
    onUserClick?: (user: CollaborationPresenceUser) => void;
    ariaLabel?: string;
    direction?: 'row' | 'column';
};

const SIZE_CLASSES = {
    sm: { avatar: 'size-7', text: 'text-[10px]' },
    md: { avatar: 'size-8', text: 'text-[11px]' },
} as const;

export function PresenceAvatarStack({
    users,
    maxVisible,
    size = 'md',
    onUserClick,
    ariaLabel,
    direction = 'row',
}: PresenceAvatarStackProps) {
    const visibleUsers = users.slice(0, maxVisible);
    const remainingCount = users.length - visibleUsers.length;
    const sizeClass = SIZE_CLASSES[size];

    return (
        <TooltipProvider delayDuration={200}>
            <div
                className={`flex items-center ${direction === 'column' ? 'flex-col -space-y-2 flex-col-reverse' : 'flex-row -space-x-2'}`}
                aria-label={ariaLabel}
            >
                {visibleUsers.map(user => (
                    <Tooltip key={user.clientId}>
                        <TooltipTrigger asChild>
                            <div className="rounded-full">
                                <Avatar className={cn(sizeClass.avatar, 'border-2 border-white text-black')}>
                                    <AvatarFallback
                                        className={cn(
                                            sizeClass.text,
                                            'font-semibold select-none',
                                            onUserClick ? 'cursor-pointer' : 'cursor-default',
                                        )}
                                        style={{ backgroundColor: user.color }}
                                        onClick={onUserClick ? () => onUserClick(user) : undefined}
                                    >
                                        {user.initials}
                                    </AvatarFallback>
                                </Avatar>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side={direction === 'column' ? 'left' : 'bottom'}>{user.name}</TooltipContent>
                    </Tooltip>
                ))}
                {remainingCount > 0 ? (
                    <div className="rounded-full">
                        <Avatar className={cn(sizeClass.avatar, 'border-2 border-white text-black')}>
                            <AvatarFallback className={cn(sizeClass.text, 'font-semibold select-none')}>+{remainingCount}</AvatarFallback>
                        </Avatar>
                    </div>
                ) : null}
            </div>
        </TooltipProvider>
    );
}
